import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/input"
import { SectionFooter } from "@/components/ui/section-footer"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"

import { useFormTabsStore } from "../../components"
import { useCheckinForm } from "../../stores/useCheckinFormStore"
import type { AccountProps } from "../../types"
import SchoolTeam from "../fields/school_team/school_team"

import { supportServicesSchema } from "./support_services.schema"

interface ISupportServices {
  account: AccountProps
  bookingContact: string
  readOnly?: boolean
}

const SupportServices: React.FC<ISupportServices> = ({
  account,
  bookingContact,
  readOnly = false,
}) => {
  const defaultData = useCheckinForm(
    useShallow((s) =>
      s.getDefaultValues(supportServicesSchema.keyof().options),
    ),
  )

  const updateForm = useCheckinForm((s) => s.updateForm)

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted },
    subscribe,
  } = useForm({
    resolver: readOnly ? undefined : zodResolver(supportServicesSchema),
    mode: "onSubmit",
    reValidateMode: "onBlur",
    defaultValues: {
      wellbeing_staff: defaultData.wellbeing_staff || [bookingContact],
      after_hours_contact: defaultData.after_hours_contact || [bookingContact],
    },
    disabled: readOnly,
  })

  useEffect(() => {
    // make sure to unsubscribe;
    const callback = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        updateForm(values)
      },
    })

    return () => callback()
  }, [subscribe, updateForm])

  const onSubmit = useFormTabsStore((s) => s.onSubmit)
  const handleBack = useFormTabsStore((s) => s.onBack)
  const setTabState = useFormTabsStore((s) => s.setStatus)

  const handleFormSubmit = handleSubmit(
    (data) => {
      setTabState("complete")
      updateForm(data)
      onSubmit()
    },
    () => {
      setTabState("error")
    },
  )

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="flex flex-col gap-6">
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>
                Follow On Support Services
              </SectionHeader.Heading>
              <SectionHeader.Subheading>
                School wellbeing and after-hours contacts
              </SectionHeader.Subheading>
            </div>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Wellbeing Staff"
              className="max-lg:hidden"
            />
            <div>
              <Label className="pb-2 lg:hidden">School Wellbeing Staff</Label>
              <Controller
                control={control}
                name="wellbeing_staff"
                render={({
                  field: { value, onChange, disabled },
                  fieldState: { error },
                }) => (
                  <SchoolTeam
                    accountId={account.id}
                    onChange={onChange}
                    value={value}
                    error={error}
                    isDisabled={disabled}
                  />
                )}
              />
            </div>
          </div>

          <hr className="bg-border-secondary h-px w-full border-none" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="After-hours Contact"
              className="max-lg:hidden"
            />
            <div>
              <Label className="pb-2 lg:hidden">
                School after hours contact
              </Label>
              <Controller
                control={control}
                name="after_hours_contact"
                render={({
                  field: { value, onChange, disabled },
                  fieldState: { error },
                }) => (
                  <SchoolTeam
                    accountId={account.id}
                    onChange={onChange}
                    value={value}
                    error={error}
                    isDisabled={disabled}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button color="secondary" onPress={handleBack}>
              Back
            </Button>
            <Button
              isDisabled={(isSubmitted && !isValid) || readOnly}
              color="primary"
              type="submit"
            >
              Submit
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      </div>
    </form>
  )
}

export default SupportServices
